import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {HeaderCloseIcon} from '@amblique/king-living/app/components/icons';
import {PromoBannerItem} from '@amblique/king-living/app/contentful/models/promo-banner-item';

/**
 * Default rotation interval in milliseconds (5 seconds)
 */
const DEFAULT_ROTATION_INTERVAL = 5000;

/**
 * Animation duration in milliseconds
 */
const ANIMATION_DURATION = 500;

/**
 * Minimum swipe distance to trigger slide change (in pixels)
 */
const MIN_SWIPE_DISTANCE = 30;

/**
 * Cooldown period after user interaction before auto-rotate resumes (in ms)
 */
const INTERACTION_COOLDOWN = 3000;

/**
 * PromoBanner component - Promotion Ribbon Block (CF-03)
 * Displays a full-width promotional banner above the header with auto-rotating messages.
 * Supports scroll and swipe gestures to navigate between items.
 * Can be dismissed by clicking the close button.
 *
 * @param {Object} props - Component props
 * @param {Contentful.PromoBanner} props.component - PromoBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {number} [props.rotationInterval] - Interval between rotations in ms (default: 5000)
 * @returns {React.ReactElement}
 */
export const PromoBanner = ({
  component,
  isComplete = true,
  isFetching = false,
  rotationInterval = DEFAULT_ROTATION_INTERVAL,
  ...props
}) => {
  const {backgroundColor, textColor, itemsCollection} = component;
  const items = (itemsCollection?.items || []).filter(Boolean);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [exitingIndex, setExitingIndex] = useState(null);
  const [direction, setDirection] = useState('up'); // 'up' or 'down'
  const [isDismissed, setIsDismissed] = useState(false);

  const timeoutRef = useRef(null);
  const displayIndexRef = useRef(displayIndex);
  const isAnimatingRef = useRef(false);
  const touchStartRef = useRef(null);
  const lastInteractionRef = useRef(0);
  const containerRef = useRef(null);

  // Keep ref in sync with state
  displayIndexRef.current = displayIndex;

  const styles = useMultiStyleConfig('contentful/promo-banner', {
    component,
    backgroundColor,
    textColor,
    isComplete,
    isFetching,
  });

  /**
   * Handle close button click
   */
  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  /**
   * Navigate to next item
   */
  const goToNext = useCallback(() => {
    if (items.length <= 1 || isAnimatingRef.current) return;

    const currentIndex = displayIndexRef.current;
    const nextIndex = (currentIndex + 1) % items.length;

    isAnimatingRef.current = true;
    setDirection('up');
    setExitingIndex(currentIndex);
    setDisplayIndex(nextIndex);

    timeoutRef.current = setTimeout(() => {
      setExitingIndex(null);
      isAnimatingRef.current = false;
    }, ANIMATION_DURATION);
  }, [items.length]);

  /**
   * Navigate to previous item
   */
  const goToPrev = useCallback(() => {
    if (items.length <= 1 || isAnimatingRef.current) return;

    const currentIndex = displayIndexRef.current;
    const prevIndex = (currentIndex - 1 + items.length) % items.length;

    isAnimatingRef.current = true;
    setDirection('down');
    setExitingIndex(currentIndex);
    setDisplayIndex(prevIndex);

    timeoutRef.current = setTimeout(() => {
      setExitingIndex(null);
      isAnimatingRef.current = false;
    }, ANIMATION_DURATION);
  }, [items.length]);

  /**
   * Handle wheel/scroll events
   */
  const handleWheel = useCallback(
    (e) => {
      if (items.length <= 1) return;

      // Prevent default scroll behavior
      e.preventDefault();

      // Update last interaction time
      lastInteractionRef.current = Date.now();

      if (e.deltaY > 0) {
        goToNext();
      } else if (e.deltaY < 0) {
        goToPrev();
      }
    },
    [items.length, goToNext, goToPrev]
  );

  /**
   * Handle touch start
   */
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = {
      y: e.touches[0].clientY,
      time: Date.now(),
    };
  }, []);

  /**
   * Handle touch end (swipe detection)
   */
  const handleTouchEnd = useCallback(
    (e) => {
      if (!touchStartRef.current || items.length <= 1) return;

      const touchEnd = e.changedTouches[0].clientY;
      const deltaY = touchStartRef.current.y - touchEnd;

      // Update last interaction time
      lastInteractionRef.current = Date.now();

      // Check if swipe distance is sufficient
      if (Math.abs(deltaY) > MIN_SWIPE_DISTANCE) {
        if (deltaY > 0) {
          // Swiped up -> go to next
          goToNext();
        } else {
          // Swiped down -> go to previous
          goToPrev();
        }
      }

      touchStartRef.current = null;
    },
    [items.length, goToNext, goToPrev]
  );

  /**
   * Auto-rotate effect (pauses after user interaction)
   */
  useEffect(() => {
    if (items.length <= 1 || isDismissed) return;

    const intervalId = setInterval(() => {
      // Check if enough time has passed since last interaction
      const timeSinceInteraction = Date.now() - lastInteractionRef.current;
      if (timeSinceInteraction >= INTERACTION_COOLDOWN) {
        goToNext();
      }
    }, rotationInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [items.length, rotationInterval, goToNext, isDismissed]);

  /**
   * Add wheel event listener with passive: false
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container || items.length <= 1) return;

    container.addEventListener('wheel', handleWheel, {passive: false});

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, items.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Don't render if dismissed or no items
  if (isDismissed || items.length === 0) {
    return null;
  }

  const currentItem = items[displayIndex];
  const exitingItem = exitingIndex !== null ? items[exitingIndex] : null;

  // Animation keyframes based on direction
  const exitAnimation =
    direction === 'up'
      ? {
          '@keyframes slideOutUp': {
            '0%': {transform: 'translateY(0)', opacity: 1},
            '50%': {opacity: 0},
            '100%': {transform: 'translateY(-100%)', opacity: 0},
          },
          animation: `slideOutUp ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
        }
      : {
          '@keyframes slideOutDown': {
            '0%': {transform: 'translateY(0)', opacity: 1},
            '50%': {opacity: 0},
            '100%': {transform: 'translateY(100%)', opacity: 0},
          },
          animation: `slideOutDown ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
        };

  const enterAnimation =
    direction === 'up'
      ? {
          '@keyframes slideInUp': {
            '0%': {transform: 'translateY(100%)', opacity: 0},
            '50%': {opacity: 1},
            '100%': {transform: 'translateY(0)', opacity: 1},
          },
          animation: `slideInUp ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
        }
      : {
          '@keyframes slideInDown': {
            '0%': {transform: 'translateY(-100%)', opacity: 0},
            '50%': {opacity: 1},
            '100%': {transform: 'translateY(0)', opacity: 1},
          },
          animation: `slideInDown ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
        };

  return (
    <Box
      ref={containerRef}
      {...styles.root}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      cursor={items.length > 1 ? 'grab' : 'default'}
      {...props}
    >
      <Box {...styles.container}>
        {/* Exiting item */}
        {exitingItem && (
          <Box {...styles.itemWrapper} position="absolute" sx={exitAnimation}>
            <PromoBannerItem
              component={exitingItem}
              isComplete={isComplete}
              isFetching={isFetching}
            />
          </Box>
        )}

        {/* Current item */}
        <Box {...styles.itemWrapper} sx={exitingIndex !== null ? enterAnimation : {}}>
          <PromoBannerItem
            component={currentItem}
            isComplete={isComplete}
            isFetching={isFetching}
          />
        </Box>
      </Box>

      {/* Close button */}
      <Box
        as="button"
        type="button"
        aria-label="Close promotion banner"
        onClick={handleDismiss}
        {...styles.closeButton}
      >
        <HeaderCloseIcon aria-hidden boxSize="16px" h="16px" viewBox="0 0 20 20" w="16px" />
      </Box>
    </Box>
  );
};

PromoBanner.displayName = 'PromoBanner';
PromoBanner.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  rotationInterval: PropTypes.number,
};
